import {
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from 'react';
import type {
  CreateRecipeInput,
  Recipe,
  RecipeIngredient,
  RecipeStatus,
  RecipeTag,
  RecipeTagKind,
} from '@repo/types';
import {
  RECIPE_DIFFICULTIES,
  RECIPE_MEALS,
  RECIPE_SPICE_LABELS,
  RECIPE_SPICE_MAX,
  RECIPE_STATUSES,
  createIngredientRefRegex,
  formatIngredientRef,
} from '@repo/types';
import { C } from '@/integrations/whoop-theme';
import { PepperIcon } from '@/components/pepper-icon';
import { useAuth } from '@/auth/auth-context';
import { createRecipe, createRecipeTag, setRecipeStatus } from '@/recipes/api';
import { RECIPE_UNIT_GROUPS } from '@/recipes/recipe-units';
import { uploadRecipeImage } from '@/recipes/upload-recipe-image';
import './recipes.css';

/**
 * The recipe detail modal (Recipes.dc.html), in two modes sharing one layout so
 * the admin authoring experience IS the rendered card. `view` renders a recipe
 * read-only with the servings/spice dials rescaling every amount. `create`
 * renders the same skeleton with each slot swapped for its editable
 * counterpart in place: the title is typed where the h2 sits, tags and the
 * difficulty/meal tiles are "+ add" pickers, and ingredients/steps are edited
 * inside their own columns (rows drag to reorder by the grip / step number).
 * Save runs the admin-only createRecipe mutation.
 * (An `edit` mode for existing recipes can hang off the same seam once an
 * updateRecipe mutation exists.)
 */
type RecipeModalProps =
  | {
      mode: 'view';
      recipe: Recipe;
      servings: number;
      spice: number;
      onClose: () => void;
      onServingsChange: (next: number) => void;
      onSpiceChange: (next: number) => void;
      /** The admin's status toggle resolves to the updated recipe. */
      onUpdated: (recipe: Recipe) => void;
    }
  | {
      mode: 'create';
      /** The DB-backed cuisine/meat vocabularies (recipe_tags), fetched by the page. */
      tags: RecipeTag[];
      onClose: () => void;
      onCreated: (recipe: Recipe) => void;
      /** A tag added from a picker, so the page's filters learn it immediately. */
      onTagCreated: (tag: RecipeTag) => void;
    };

/** One ingredient row as typed; quantity stays a string until submit ("0.5" mid-keystroke). */
interface IngredientDraft {
  /** Minted per row so steps can reference the ingredient before it's saved. */
  id: string;
  quantity: string;
  unit: string;
  name: string;
  spiced: boolean;
  /**
   * True while the unit select sits on custom… and `unit` holds free text. An
   * explicit flag, not derived: an empty custom unit would otherwise be
   * indistinguishable from the "units" (bare count) preset.
   */
  customUnit: boolean;
}

function newIngredientDraft(): IngredientDraft {
  // Short and opaque: the token also lives inline in step text while authoring.
  return {
    id: crypto.randomUUID().slice(0, 8),
    quantity: '',
    unit: '',
    name: '',
    spiced: false,
    customUnit: false,
  };
}

/** Unit-select sentinel for the custom… option; never stored as a unit itself. */
const CUSTOM_UNIT = '__custom__';

/** The two drag-reorderable columns of the create form. */
type ReorderList = 'ingredients' | 'steps';

export function RecipeModal(props: RecipeModalProps) {
  const editing = props.mode === 'create';
  const { isAdmin } = useAuth();

  // Draft state exists in both modes (hooks must be unconditional); only the
  // create branch reads it.
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<RecipeStatus>('draft');
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [cuisines, setCuisines] = useState<string[]>([]);
  const [proteins, setProteins] = useState<string[]>([]);
  const [meal, setMeal] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [timeMinutes, setTimeMinutes] = useState('');
  const [calories, setCalories] = useState('');
  const [baseServings, setBaseServings] = useState(4);
  const [baseSpice, setBaseSpice] = useState(0);
  const [imageURL, setImageURL] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [rows, setRows] = useState<IngredientDraft[]>(() => [newIngredientDraft()]);
  const [draftSteps, setDraftSteps] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [openPicker, setOpenPicker] = useState<string | null>(null);
  /** In-flight ingredient/step reorder: the row being dragged and its target slot. */
  const [rowDrag, setRowDrag] = useState<{
    list: ReorderList;
    from: number;
    to: number;
  } | null>(null);
  /** Step textareas by index, so a picked reference lands at the caret. */
  const stepFields = useRef<(HTMLTextAreaElement | null)[]>([]);
  /** Hidden file input behind the "Upload photo" button. */
  const photoField = useRef<HTMLInputElement | null>(null);
  /** Drag-enter/-leave pair up per child, so plain booleans would flicker. */
  const dragDepth = useRef(0);

  const recipe = props.mode === 'view' ? props.recipe : null;
  const servings = props.mode === 'view' ? props.servings : baseServings;
  const spice = props.mode === 'view' ? props.spice : baseSpice;

  // Picker options for the DB-backed vocabularies (view mode renders plain tags).
  const vocabulary = props.mode === 'create' ? props.tags : [];
  const cuisineOptions = vocabulary
    .filter(({ kind }) => kind === 'cuisine')
    .map(({ name }) => name);
  const proteinOptions = vocabulary
    .filter(({ kind }) => kind === 'protein')
    .map(({ name }) => name);

  // View mode: every amount rescales with the dials. Create mode: the typed
  // amounts ARE the base, so the list renders them verbatim.
  const scaledIngredients = recipe
    ? recipe.ingredients.map((ingredient) => {
        const servingFactor = servings / recipe.servings;
        const spiceFactor = recipe.spice > 0 ? spice / recipe.spice : 1;
        const multiplier = servingFactor * (ingredient.spiced ? spiceFactor : 1);
        return {
          id: ingredient.id,
          text: formatIngredient({ ingredient, quantity: ingredient.quantity * multiplier }),
          spiced: ingredient.spiced,
        };
      })
    : [];
  // Steps may reference ingredients ({{ing:<id>}} tokens); substituting from the
  // scaled list above keeps the amounts inside step text in lockstep with the
  // servings and spice dials.
  const scaledTextByID = new Map(scaledIngredients.map(({ id, text }) => [id, text]));

  let spiceNote = '';
  if (recipe) {
    if (recipe.spice === 0) spiceNote = 'No spicy ingredients — add chili to taste';
    else if (spice !== recipe.spice)
      spiceNote = `Spiced ingredients scaled ×${Math.round((spice / recipe.spice) * 100) / 100}`;
  }

  function commitServings(next: number) {
    const clamped = Math.max(1, Math.min(99, next));
    if (props.mode === 'view') props.onServingsChange(clamped);
    else setBaseServings(clamped);
  }

  function commitSpice(next: number) {
    if (props.mode === 'view') props.onSpiceChange(next);
    else setBaseSpice(next);
  }

  function patchRow(index: number, patch: Partial<IngredientDraft>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  /**
   * Reordering: a row drags by its handle (the ingredient grip / the step
   * number) and drops anywhere on its own list. While dragging, state tracks
   * the slot under the pointer so that row shows an insertion edge; the array
   * reorders once, on drop — no DOM moves mid-drag, which some browsers answer
   * by cancelling the drag.
   */
  function startRowDrag({
    event,
    list,
    index,
  }: {
    event: DragEvent<HTMLElement>;
    list: ReorderList;
    index: number;
  }) {
    // Firefox refuses to start a drag whose store holds no data; the payload
    // itself is never read.
    event.dataTransfer.setData('text/plain', '');
    event.dataTransfer.effectAllowed = 'move';
    // The handle is the draggable, but the whole row makes the better ghost.
    const row = event.currentTarget.parentElement;
    if (row) {
      const { left, top } = row.getBoundingClientRect();
      event.dataTransfer.setDragImage(row, event.clientX - left, event.clientY - top);
    }
    setRowDrag({ list, from: index, to: index });
  }

  function enterRow({ list, index }: { list: ReorderList; index: number }) {
    // Same-reference return skips the re-render while crossing a row's children.
    setRowDrag((prev) =>
      prev && prev.list === list && prev.to !== index ? { ...prev, to: index } : prev,
    );
  }

  function allowRowDrop({ event, list }: { event: DragEvent<HTMLElement>; list: ReorderList }) {
    if (rowDrag?.list !== list) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function commitRowDrop({ event, list }: { event: DragEvent<HTMLElement>; list: ReorderList }) {
    if (rowDrag?.list !== list) return;
    event.preventDefault();
    const { from, to } = rowDrag;
    if (from !== to) {
      if (list === 'ingredients') setRows((prev) => moveItem({ items: prev, from, to }));
      else setDraftSteps((prev) => moveItem({ items: prev, from, to }));
    }
    setRowDrag(null);
  }

  /** The dragged row dims; the row it would land at shows an insertion edge. */
  function rowDragStyle({ list, index }: { list: ReorderList; index: number }): CSSProperties {
    if (rowDrag === null || rowDrag.list !== list) return {};
    if (index === rowDrag.from) return { opacity: 0.45 };
    if (index !== rowDrag.to) return {};
    return rowDrag.to < rowDrag.from
      ? { boxShadow: 'inset 0 2px 0 0 rgba(181,155,255,0.7)' }
      : { boxShadow: 'inset 0 -2px 0 0 rgba(181,155,255,0.7)' };
  }

  function pick(value: string, setter: (next: string) => void) {
    setter(value);
    setOpenPicker(null);
  }

  /** Multi pickers toggle membership and stay open, so several tags can be picked in one visit. */
  function toggleTag(value: string, setter: (update: (prev: string[]) => string[]) => void) {
    setter((prev) =>
      prev.includes(value) ? prev.filter((picked) => picked !== value) : [...prev, value],
    );
  }

  /**
   * "+ add new" from a vocabulary picker: a name that only differs by case
   * reuses the existing tag; anything else is stored through the admin-only
   * createRecipeTag mutation. Either way the tag joins the selection (the
   * picker stays open — these are multi-selects). Failures propagate to the
   * picker's add row, which displays them.
   */
  async function addTag({ kind, name }: { kind: RecipeTagKind; name: string }) {
    if (props.mode !== 'create') return;
    const setter = kind === 'cuisine' ? setCuisines : setProteins;
    const options = kind === 'cuisine' ? cuisineOptions : proteinOptions;
    const existing = options.find((option) => option.toLowerCase() === name.toLowerCase());
    if (existing) {
      setter((prev) => (prev.includes(existing) ? prev : [...prev, existing]));
      return;
    }
    const created = await createRecipeTag({ kind, name });
    props.onTagCreated(created);
    setter((prev) => [...prev, created.name]);
  }

  /** View mode, admin only: flip draft ⇄ complete through the setRecipeStatus mutation. */
  async function changeStatus(next: string) {
    setOpenPicker(null);
    if (props.mode !== 'view' || !recipe) return;
    if (next !== 'draft' && next !== 'complete') return;
    if (next === recipe.status || statusBusy) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      const updated = await setRecipeStatus({ id: recipe.id, status: next });
      props.onUpdated(updated);
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : String(error));
    } finally {
      setStatusBusy(false);
    }
  }

  function insertIngredientRef({
    stepIndex,
    ingredientID,
  }: {
    stepIndex: number;
    ingredientID: string;
  }) {
    const token = formatIngredientRef(ingredientID);
    const field = stepFields.current[stepIndex];
    setDraftSteps((prev) =>
      prev.map((step, i) => {
        if (i !== stepIndex) return step;
        const at = field?.selectionStart ?? step.length;
        return `${step.slice(0, at)}${token}${step.slice(at)}`;
      }),
    );
    field?.focus();
  }

  function buildInput(): { input: CreateRecipeInput } | { error: string } {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return { error: 'Give the recipe a title.' };
    if (cuisines.length === 0) return { error: 'Pick at least one cuisine.' };
    if (proteins.length === 0) return { error: 'Pick at least one meat.' };
    if (!meal) return { error: 'Pick a meal.' };
    if (!difficulty) return { error: 'Pick a difficulty.' };

    const parsedTime = parseWholeNumber(timeMinutes);
    if (parsedTime === null || parsedTime < 1) {
      return { error: 'Total time must be a whole number of minutes, at least 1.' };
    }
    let parsedCalories: number | null = null;
    if (calories.trim()) {
      parsedCalories = parseWholeNumber(calories);
      if (parsedCalories === null || parsedCalories < 1) {
        return { error: 'Calories per serving must be a whole number, at least 1.' };
      }
    }

    // Untouched rows are scaffolding, not input — drop them before validating.
    const touched = rows.filter((row) => row.quantity.trim() || row.unit.trim() || row.name.trim());
    if (touched.length === 0) return { error: 'Add at least one ingredient.' };
    const parsedIngredients: RecipeIngredient[] = [];
    for (const [index, row] of touched.entries()) {
      const name = row.name.trim();
      if (!name) return { error: `Ingredient ${index + 1} needs a name.` };
      const quantity = Number(row.quantity);
      if (!row.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
        return { error: `Ingredient ${index + 1} ("${name}") needs a quantity above 0.` };
      }
      parsedIngredients.push({
        id: row.id,
        quantity,
        unit: row.unit.trim(),
        name,
        spiced: row.spiced,
      });
    }

    const trimmedSteps = draftSteps.map((step) => step.trim()).filter(Boolean);
    if (trimmedSteps.length === 0) return { error: 'Add at least one step.' };
    // Mirror create-recipe's validateStepRefs: a reference must point at a listed
    // ingredient (removing a referenced row invalidates its tokens).
    const ingredientIDs = new Set(parsedIngredients.map(({ id }) => id));
    for (const [index, step] of trimmedSteps.entries()) {
      for (const match of step.matchAll(createIngredientRefRegex())) {
        const refID = match[1];
        if (refID !== undefined && !ingredientIDs.has(refID)) {
          return { error: `Step ${index + 1} references an ingredient that is no longer listed.` };
        }
      }
    }

    return {
      input: {
        title: trimmedTitle,
        cuisines,
        proteins,
        meal,
        difficulty,
        timeMinutes: parsedTime,
        calories: parsedCalories,
        servings: baseServings,
        spice: baseSpice,
        imageURL: imageURL.trim() || null,
        status,
        ingredients: parsedIngredients,
        steps: trimmedSteps,
      },
    };
  }

  async function handleSave() {
    if (props.mode !== 'create') return;
    const outcome = buildInput();
    if ('error' in outcome) {
      setError(outcome.error);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const created = await createRecipe(outcome.input);
      props.onCreated(created);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
      setSaving(false);
    }
  }

  /** Upload a dropped/pasted/picked photo and drop the stored URL into the draft. */
  async function handlePhotoFile(file: Blob | null | undefined) {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      setImageURL(await uploadRecipeImage(file));
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setUploading(false);
    }
  }

  const draftImage = imageURL.trim();
  // Uploaded photos are root-relative /images/… paths; hand-typed art is absolute.
  const heroImageURL = recipe
    ? recipe.imageURL
    : /^(https?:\/\/|\/images\/)/.test(draftImage)
      ? draftImage
      : null;

  return (
    <div
      // A stray backdrop click must never discard a half-typed recipe, so only
      // the read-only mode closes this way.
      onClick={editing ? undefined : props.onClose}
      className="rp-scroll"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(6,6,8,0.74)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        // Above the sticky WorkspaceHeader (zIndex 50): an open recipe covers
        // all site chrome, not just the page body.
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        // Pasting a copied image anywhere in the form uploads it; text pastes
        // (clipboardData.files is empty) still reach the fields untouched.
        onPaste={
          editing
            ? (event) => {
                const file = Array.from(event.clipboardData.files).find(({ type }) =>
                  type.startsWith('image/'),
                );
                if (file) {
                  event.preventDefault();
                  void handlePhotoFile(file);
                }
              }
            : undefined
        }
        className="rp-pop"
        style={{
          width: '100%',
          maxWidth: 1040,
          background: '#141416',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 40px 100px rgba(0,0,0,0.65)',
        }}
      >
        <div
          onDragEnter={
            editing
              ? (event) => {
                  event.preventDefault();
                  // Row-reorder drags from the lists below can wander over the
                  // hero; only a drag carrying files is a photo drop.
                  if (!event.dataTransfer.types.includes('Files')) return;
                  dragDepth.current += 1;
                  setDragging(true);
                }
              : undefined
          }
          onDragOver={
            editing
              ? (event) => {
                  if (event.dataTransfer.types.includes('Files')) event.preventDefault();
                }
              : undefined
          }
          onDragLeave={
            editing
              ? () => {
                  dragDepth.current = Math.max(0, dragDepth.current - 1);
                  if (dragDepth.current === 0) setDragging(false);
                }
              : undefined
          }
          onDrop={
            editing
              ? (event) => {
                  event.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  void handlePhotoFile(event.dataTransfer.files[0]);
                }
              : undefined
          }
          style={{
            position: 'relative',
            height: 248,
            background: '#0f0f11',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            backgroundImage:
              'repeating-linear-gradient(135deg,rgba(255,255,255,0.018) 0px,rgba(255,255,255,0.018) 1px,transparent 1px,transparent 9px)',
            ...(dragging ? { outline: '2px dashed rgba(255,255,255,0.4)', outlineOffset: -8 } : {}),
          }}
        >
          {heroImageURL !== null ? (
            <img
              src={heroImageURL}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <>
              <ImageIcon />
              <div
                style={{
                  font: "500 11px 'JetBrains Mono',monospace",
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: C.text3,
                }}
              >
                {recipe
                  ? recipe.cuisines.join(' · ')
                  : cuisines.length > 0
                    ? cuisines.join(' · ')
                    : 'New recipe'}
              </div>
              {editing && (
                <div style={{ font: "500 12px 'Hanken Grotesk',sans-serif", color: C.text3 }}>
                  Drag &amp; drop a photo, paste one, or upload below
                </div>
              )}
            </>
          )}
          {editing && (
            <div
              style={{
                position: 'absolute',
                bottom: 14,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(520px, 86%)',
                display: 'flex',
                gap: 8,
              }}
            >
              <input
                ref={photoField}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  void handlePhotoFile(event.target.files?.[0]);
                  // Same file re-picked after an error must re-fire onChange.
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => photoField.current?.click()}
                disabled={uploading}
                style={{
                  flexShrink: 0,
                  height: 34,
                  padding: '0 14px',
                  borderRadius: 9,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(12,12,14,0.7)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: C.text,
                  font: "600 12.5px 'Hanken Grotesk',sans-serif",
                  cursor: uploading ? 'default' : 'pointer',
                  opacity: uploading ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {uploading ? 'Uploading…' : 'Upload photo'}
              </button>
              <input
                className="ww-input"
                value={imageURL}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setImageURL(event.target.value)}
                placeholder="… or paste an https:// image URL"
                aria-label="Image URL"
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  height: 34,
                  padding: '0 12px',
                  borderRadius: 9,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(12,12,14,0.7)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: C.text,
                  font: "500 12.5px 'Hanken Grotesk',sans-serif",
                  outline: 'none',
                }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="ww-btn"
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              width: 34,
              height: 34,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(12,12,14,0.7)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              color: C.text,
              fontSize: 20,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '22px 26px 30px' }}>
          {/* Click-away closes an open picker without touching anything else. */}
          {openPicker !== null && (
            <div
              onClick={() => setOpenPicker(null)}
              style={{ position: 'fixed', inset: 0, zIndex: 9 }}
            />
          )}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {recipe ? (
              <>
                {[...recipe.cuisines, ...recipe.proteins].map((label) => (
                  <span key={label} style={TAG_STYLE}>
                    {label}
                  </span>
                ))}
                {/* Status is admin chrome: guests never see drafts, so the tag
                    (and its toggle) would only state the obvious. */}
                {isAdmin && (
                  <OptionPicker
                    label="Status"
                    options={RECIPE_STATUSES}
                    selected={[recipe.status]}
                    isOpen={openPicker === 'Status'}
                    onToggle={() => setOpenPicker((prev) => (prev === 'Status' ? null : 'Status'))}
                    onSelect={changeStatus}
                    trigger="tag"
                    triggerStyle={recipe.status === 'draft' ? draftTagStyle : completeTagStyle}
                  />
                )}
                {statusError && (
                  <span
                    role="alert"
                    style={{ font: "500 12px 'Hanken Grotesk',sans-serif", color: C.low }}
                  >
                    {statusError}
                  </span>
                )}
              </>
            ) : (
              <>
                <OptionPicker
                  label="Cuisine"
                  options={cuisineOptions}
                  selected={cuisines}
                  isOpen={openPicker === 'Cuisine'}
                  onToggle={() => setOpenPicker((prev) => (prev === 'Cuisine' ? null : 'Cuisine'))}
                  onSelect={(option) => toggleTag(option, setCuisines)}
                  trigger="tag"
                  onAdd={(name) => addTag({ kind: 'cuisine', name })}
                />
                <OptionPicker
                  label="Meat"
                  options={proteinOptions}
                  selected={proteins}
                  isOpen={openPicker === 'Meat'}
                  onToggle={() => setOpenPicker((prev) => (prev === 'Meat' ? null : 'Meat'))}
                  onSelect={(option) => toggleTag(option, setProteins)}
                  trigger="tag"
                  onAdd={(name) => addTag({ kind: 'protein', name })}
                />
                <OptionPicker
                  label="Status"
                  options={RECIPE_STATUSES}
                  selected={[status]}
                  isOpen={openPicker === 'Status'}
                  onToggle={() => setOpenPicker((prev) => (prev === 'Status' ? null : 'Status'))}
                  onSelect={(option) => {
                    if (option === 'draft' || option === 'complete') {
                      setStatus(option);
                      setOpenPicker(null);
                    }
                  }}
                  trigger="tag"
                  triggerStyle={status === 'draft' ? draftTagStyle : completeTagStyle}
                />
              </>
            )}
          </div>

          {recipe ? (
            <h2 style={TITLE_STYLE}>{recipe.title}</h2>
          ) : (
            <input
              className="ww-input"
              value={title}
              autoFocus
              onChange={(event: ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)}
              placeholder="Name the recipe…"
              aria-label="Title"
              style={{
                ...TITLE_STYLE,
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px dashed rgba(255,255,255,0.18)',
                paddingBottom: 6,
                outline: 'none',
              }}
            />
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))',
              gap: 10,
              marginBottom: 20,
            }}
          >
            <StatTile
              label="Time"
              value={
                recipe ? (
                  formatTimeText(recipe.timeMinutes)
                ) : (
                  <>
                    <input
                      className="ww-input rp-num"
                      value={timeMinutes}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setTimeMinutes(event.target.value)
                      }
                      placeholder="time"
                      inputMode="numeric"
                      aria-label="Total time in minutes"
                      style={statInputStyle}
                    />{' '}
                    <span style={statSuffixStyle}>min</span>
                  </>
                )
              }
            />
            {(editing || (recipe !== null && recipe.calories !== null)) && (
              <StatTile
                label="Calories"
                value={
                  recipe ? (
                    <>
                      {recipe.calories} <span style={statSuffixStyle}>/ serving</span>
                    </>
                  ) : (
                    <>
                      <input
                        className="ww-input rp-num"
                        value={calories}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setCalories(event.target.value)
                        }
                        placeholder="optional"
                        inputMode="numeric"
                        aria-label="Calories per serving"
                        style={statInputStyle}
                      />{' '}
                      <span style={statSuffixStyle}>/ serving</span>
                    </>
                  )
                }
              />
            )}
            <StatTile
              label="Difficulty"
              value={
                recipe ? (
                  recipe.difficulty
                ) : (
                  <OptionPicker
                    label="Difficulty"
                    options={RECIPE_DIFFICULTIES}
                    selected={difficulty ? [difficulty] : []}
                    isOpen={openPicker === 'Difficulty'}
                    onToggle={() =>
                      setOpenPicker((prev) => (prev === 'Difficulty' ? null : 'Difficulty'))
                    }
                    onSelect={(option) => pick(option, setDifficulty)}
                    trigger="stat"
                  />
                )
              }
            />
            <StatTile
              label="Meal"
              value={
                recipe ? (
                  recipe.meal
                ) : (
                  <OptionPicker
                    label="Meal"
                    options={RECIPE_MEALS}
                    selected={meal ? [meal] : []}
                    isOpen={openPicker === 'Meal'}
                    onToggle={() => setOpenPicker((prev) => (prev === 'Meal' ? null : 'Meal'))}
                    onSelect={(option) => pick(option, setMeal)}
                    trigger="stat"
                  />
                )
              }
            />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={dialTileStyle}>
              <div style={{ ...MONO_LABEL, color: C.text3, marginBottom: 10 }}>Servings</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <button
                  type="button"
                  onClick={() => commitServings(servings - 1)}
                  className="ww-btn"
                  style={stepperStyle}
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  className="rp-num"
                  value={servings}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const parsed = parseInt(event.target.value, 10);
                    commitServings(Number.isNaN(parsed) ? 1 : parsed);
                  }}
                  style={{
                    width: 58,
                    height: 34,
                    textAlign: 'center',
                    borderRadius: 9,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: '#141416',
                    color: C.text,
                    font: "700 16px 'Hanken Grotesk',sans-serif",
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => commitServings(servings + 1)}
                  className="ww-btn"
                  style={stepperStyle}
                >
                  +
                </button>
                <span
                  style={{
                    font: "500 11.5px 'Hanken Grotesk',sans-serif",
                    color: C.text3,
                    marginLeft: 2,
                  }}
                >
                  {editing ? 'base count the amounts are written for' : 'scales every ingredient'}
                </span>
              </div>
            </div>
            <div style={dialTileStyle}>
              <div style={{ ...MONO_LABEL, color: C.text3, marginBottom: 6 }}>Spice level</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {Array.from({ length: RECIPE_SPICE_MAX + 1 }, (_, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => commitSpice(spice === index + 1 ? index : index + 1)}
                    aria-label={`Spice level ${index + 1}`}
                    className="ww-btn"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 3,
                      cursor: 'pointer',
                      lineHeight: 0,
                    }}
                  >
                    <PepperIcon filled={index < spice} />
                  </button>
                ))}
                <span
                  style={{
                    font: "600 12px 'Hanken Grotesk',sans-serif",
                    color: C.low,
                    marginLeft: 8,
                  }}
                >
                  {RECIPE_SPICE_LABELS[spice] ?? 'Extreme'}
                </span>
              </div>
              {spiceNote && (
                <div
                  style={{
                    font: "500 11.5px 'Hanken Grotesk',sans-serif",
                    color: C.text3,
                    marginTop: 7,
                  }}
                >
                  {spiceNote}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 230 }}>
              <div style={{ ...MONO_LABEL, color: C.text2, marginBottom: 2 }}>Ingredients</div>
              <ul
                onDragOver={(event) => allowRowDrop({ event, list: 'ingredients' })}
                onDrop={(event) => commitRowDrop({ event, list: 'ingredients' })}
                style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}
              >
                {recipe
                  ? scaledIngredients.map((ingredient, index) => (
                      <li key={`${recipe.id}-ing-${index}`} style={ingredientRowStyle}>
                        <span style={ingredientDotStyle} />
                        <span style={{ flex: 1 }}>{ingredient.text}</span>
                        {ingredient.spiced && <span style={spiceBadgeStyle}>Spice</span>}
                      </li>
                    ))
                  : rows.map((row, index) => (
                      <li
                        key={row.id}
                        onDragEnter={() => enterRow({ list: 'ingredients', index })}
                        style={{
                          ...ingredientRowStyle,
                          ...rowDragStyle({ list: 'ingredients', index }),
                        }}
                      >
                        <span
                          draggable
                          onDragStart={(event) =>
                            startRowDrag({ event, list: 'ingredients', index })
                          }
                          onDragEnd={() => setRowDrag(null)}
                          aria-label={`Drag to reorder ingredient ${index + 1}`}
                          title="Drag to reorder"
                          style={dragHandleStyle}
                        >
                          <GripIcon />
                        </span>
                        <input
                          className="ww-input"
                          value={row.quantity}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            patchRow(index, { quantity: event.target.value })
                          }
                          placeholder="qty"
                          inputMode="decimal"
                          aria-label={`Ingredient ${index + 1} quantity`}
                          style={{ ...rowInputStyle, width: 52, textAlign: 'center' }}
                        />
                        {/* Unit stays a free string in the data; the select offers
                            the house vocabulary, "units" (the empty value) is a
                            bare count ("2 apples"), and custom… swaps in a text
                            field for anything else. */}
                        <select
                          className="ww-input"
                          value={row.customUnit ? CUSTOM_UNIT : row.unit}
                          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                            patchRow(
                              index,
                              event.target.value === CUSTOM_UNIT
                                ? { customUnit: true, unit: '' }
                                : { customUnit: false, unit: event.target.value },
                            )
                          }
                          aria-label={`Ingredient ${index + 1} unit`}
                          style={rowSelectStyle}
                        >
                          <option value="">units</option>
                          {RECIPE_UNIT_GROUPS.map(({ label, units }) => (
                            <optgroup key={label} label={label}>
                              {units.map((unit) => (
                                <option key={unit} value={unit}>
                                  {unit}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                          <option value={CUSTOM_UNIT}>custom…</option>
                        </select>
                        {row.customUnit && (
                          <input
                            className="ww-input"
                            value={row.unit}
                            autoFocus
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              patchRow(index, { unit: event.target.value })
                            }
                            placeholder="unit"
                            aria-label={`Ingredient ${index + 1} custom unit`}
                            style={{ ...rowInputStyle, width: 58 }}
                          />
                        )}
                        <input
                          className="ww-input"
                          value={row.name}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            patchRow(index, { name: event.target.value })
                          }
                          placeholder="ingredient"
                          aria-label={`Ingredient ${index + 1} name`}
                          style={{ ...rowInputStyle, flex: 1, minWidth: 0 }}
                        />
                        <button
                          type="button"
                          onClick={() => patchRow(index, { spiced: !row.spiced })}
                          aria-pressed={row.spiced}
                          aria-label={`Ingredient ${index + 1} scales with the spice dial`}
                          title="Scales with the spice dial"
                          className="ww-btn"
                          style={{
                            ...rowIconButtonStyle,
                            border: `1px solid ${row.spiced ? 'rgba(224,137,122,0.5)' : 'rgba(255,255,255,0.12)'}`,
                            background: row.spiced ? 'rgba(224,137,122,0.14)' : 'transparent',
                          }}
                        >
                          <PepperIcon filled={row.spiced} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                          aria-label={`Remove ingredient ${index + 1}`}
                          className="ww-btn"
                          style={rowIconButtonStyle}
                        >
                          ×
                        </button>
                      </li>
                    ))}
              </ul>
              {editing && (
                <button
                  type="button"
                  onClick={() => setRows((prev) => [...prev, newIngredientDraft()])}
                  className="ww-btn"
                  style={{ ...addRowButtonStyle, marginTop: 10 }}
                >
                  + Add ingredient
                </button>
              )}
            </div>
            <div style={{ flex: 1.2, minWidth: 270 }}>
              <div style={{ ...MONO_LABEL, color: C.text2, marginBottom: 14 }}>Method</div>
              <div
                onDragOver={(event) => allowRowDrop({ event, list: 'steps' })}
                onDrop={(event) => commitRowDrop({ event, list: 'steps' })}
                style={{ display: 'flex', flexDirection: 'column', gap: 15 }}
              >
                {recipe
                  ? recipe.steps.map((step, index) => (
                      <div key={`${recipe.id}-step-${index}`} style={{ display: 'flex', gap: 12 }}>
                        <div style={stepNumberStyle}>{index + 1}</div>
                        <div style={stepTextStyle}>{renderStepText({ step, scaledTextByID })}</div>
                      </div>
                    ))
                  : draftSteps.map((step, index) => (
                      <div
                        key={index}
                        onDragEnter={() => enterRow({ list: 'steps', index })}
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          ...rowDragStyle({ list: 'steps', index }),
                        }}
                      >
                        <div
                          draggable
                          onDragStart={(event) => startRowDrag({ event, list: 'steps', index })}
                          onDragEnd={() => setRowDrag(null)}
                          aria-label={`Drag to reorder step ${index + 1}`}
                          title="Drag to reorder"
                          style={{ ...stepNumberStyle, cursor: 'grab' }}
                        >
                          {index + 1}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                          }}
                        >
                          <textarea
                            className="ww-input"
                            value={step}
                            rows={2}
                            ref={(field) => {
                              stepFields.current[index] = field;
                            }}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                              setDraftSteps((prev) =>
                                prev.map((s, i) => (i === index ? event.target.value : s)),
                              )
                            }
                            placeholder="What happens in this step?"
                            aria-label={`Step ${index + 1}`}
                            style={{
                              ...stepTextStyle,
                              width: '100%',
                              boxSizing: 'border-box',
                              background: '#0f0f11',
                              border: '1px solid rgba(255,255,255,0.12)',
                              borderRadius: 9,
                              padding: '8px 10px',
                              resize: 'vertical',
                              outline: 'none',
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <select
                              className="ww-input"
                              value=""
                              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                                if (event.target.value) {
                                  insertIngredientRef({
                                    stepIndex: index,
                                    ingredientID: event.target.value,
                                  });
                                }
                              }}
                              aria-label={`Insert ingredient into step ${index + 1}`}
                              disabled={!rows.some((row) => row.name.trim())}
                              style={insertSelectStyle}
                            >
                              <option value="" disabled>
                                + Insert ingredient…
                              </option>
                              {rows
                                .filter((row) => row.name.trim())
                                .map((row) => (
                                  <option key={row.id} value={row.id}>
                                    {row.name.trim()}
                                  </option>
                                ))}
                            </select>
                            <StepPreview step={step} rows={rows} />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setDraftSteps((prev) => prev.filter((_, i) => i !== index))
                          }
                          aria-label={`Remove step ${index + 1}`}
                          className="ww-btn"
                          style={rowIconButtonStyle}
                        >
                          ×
                        </button>
                      </div>
                    ))}
              </div>
              {editing && (
                <button
                  type="button"
                  onClick={() => setDraftSteps((prev) => [...prev, ''])}
                  className="ww-btn"
                  style={{ ...addRowButtonStyle, marginTop: 12 }}
                >
                  + Add step
                </button>
              )}
            </div>
          </div>

          {editing && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 24,
                paddingTop: 16,
                borderTop: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div
                role="alert"
                style={{
                  flex: 1,
                  font: "500 13px 'Hanken Grotesk',sans-serif",
                  color: C.low,
                  minWidth: 0,
                }}
              >
                {error}
              </div>
              <button
                type="button"
                onClick={props.onClose}
                className="ww-btn"
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="ww-btn"
                style={{ ...saveButtonStyle, opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Saving…' : 'Save recipe'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Value-or-add picker rendered where the final value will sit: a tag-shaped
 * trigger in the tags row, a stat-value-shaped one inside a tile. Unset shows
 * a dashed "+ Label" affordance; picked shows the value(s) and reopens on
 * click. Whether picking closes the popover is the caller's choice: single
 * pickers close in onSelect, multi pickers toggle membership and stay open
 * (the click-away overlay closes them).
 */
function OptionPicker({
  label,
  options,
  selected,
  isOpen,
  onToggle,
  onSelect,
  trigger,
  triggerStyle: triggerStyleOverride,
  onAdd,
}: {
  label: string;
  options: readonly string[];
  /** The picked value(s); empty renders the dashed "+ Label" affordance. */
  selected: readonly string[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (option: string) => void;
  trigger: 'tag' | 'stat';
  /** Replaces the computed trigger look (e.g. the amber/green status tags). */
  triggerStyle?: CSSProperties;
  /** DB-backed vocabularies only: store the typed name, then select it. */
  onAdd?: (name: string) => Promise<void>;
}) {
  const hasValue = selected.length > 0;
  const triggerStyle =
    triggerStyleOverride ??
    (trigger === 'tag'
      ? hasValue
        ? { ...TAG_STYLE, border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }
        : addTagTriggerStyle
      : hasValue
        ? statPickedTriggerStyle
        : statEmptyTriggerStyle);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="ww-btn"
        aria-label={label}
        onClick={onToggle}
        style={triggerStyle}
      >
        {hasValue ? selected.join(' · ') : `+ ${label}`}
      </button>
      {isOpen && (
        <div className="rp-pop" style={pickerPopStyle}>
          <div style={{ ...MONO_LABEL, color: C.text3, marginBottom: 9 }}>{label}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {options.map((option) => (
              <button
                type="button"
                key={option}
                className="ww-btn"
                onClick={() => onSelect(option)}
                style={selected.includes(option) ? chipOnStyle : chipOffStyle}
              >
                {option}
              </button>
            ))}
          </div>
          {onAdd && <AddTagRow label={label} onAdd={onAdd} />}
        </div>
      )}
    </span>
  );
}

/**
 * The "+ add" row at the foot of a DB-backed picker: type a name, hit Add (or
 * Enter), and the tag is stored and selected. Mounted inside the popover so
 * its draft/error state resets whenever the picker closes.
 */
function AddTagRow({ label, onAdd }: { label: string; onAdd: (name: string) => Promise<void> }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Multi pickers stay open after an add, so clear the row for the next tag.
      await onAdd(trimmed);
      setDraft('');
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 7, marginTop: 11 }}>
        <input
          className="ww-input"
          value={draft}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void submit();
            }
          }}
          placeholder={`New ${label.toLowerCase()}…`}
          aria-label={`New ${label.toLowerCase()} name`}
          style={{ ...rowInputStyle, flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="ww-btn"
          style={{
            ...addRowButtonStyle,
            padding: '0 12px',
            height: 30,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Adding…' : '+ Add'}
        </button>
      </div>
      {error && (
        <div
          role="alert"
          style={{ font: "500 12px 'Hanken Grotesk',sans-serif", color: C.low, marginTop: 7 }}
        >
          {error}
        </div>
      )}
    </>
  );
}

/**
 * Author-time preview of a step that references ingredients: tokens swapped for
 * the row's base amount and name (the detail view scales them live). Rendered
 * only while the step actually holds a reference.
 */
function StepPreview({ step, rows }: { step: string; rows: IngredientDraft[] }) {
  if (!createIngredientRefRegex().test(step)) return null;
  const rowsByID = new Map(rows.map((row) => [row.id, row]));
  const preview = step.replace(createIngredientRefRegex(), (token, id: string) => {
    const row = rowsByID.get(id);
    if (!row) return token;
    const name = row.name.trim() || 'unnamed ingredient';
    return [row.quantity.trim(), row.unit.trim(), name].filter(Boolean).join(' ');
  });
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        font: "400 12px 'Hanken Grotesk',sans-serif",
        color: C.text3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={preview}
    >
      → {preview}
    </div>
  );
}

/**
 * Substitute `{{ing:<id>}}` tokens with the ingredient's scaled amount, styled
 * so the dynamic text is visibly tied to the dials. Tokens whose id isn't in
 * the recipe render verbatim — a loud signal over a silent swallow.
 */
function renderStepText({
  step,
  scaledTextByID,
}: {
  step: string;
  scaledTextByID: Map<string, string>;
}): ReactNode {
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of step.matchAll(createIngredientRefRegex())) {
    const id = match[1];
    const scaled = id === undefined ? undefined : scaledTextByID.get(id);
    if (scaled === undefined) continue;
    const start = match.index ?? 0;
    if (start > cursor) parts.push(step.slice(cursor, start));
    parts.push(
      <span key={start} style={{ color: '#c9b6ff', fontWeight: 600 }}>
        {scaled}
      </span>,
    );
    cursor = start + match[0].length;
  }
  if (parts.length === 0) return step;
  if (cursor < step.length) parts.push(step.slice(cursor));
  return parts;
}

function StatTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div
      style={{
        background: '#0f0f11',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 12,
        padding: '12px 14px',
      }}
    >
      <div style={{ ...MONO_LABEL, color: C.text3, marginBottom: 6 }}>{label}</div>
      <div style={{ font: "700 17px 'Hanken Grotesk',sans-serif", color: C.text }}>{value}</div>
    </div>
  );
}

/** Human total time: "45 min", "2 hr", "1 hr 30 min". */
function formatTimeText(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${minutes / 60} hr`;
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

/** Snap a scaled quantity to the nearest tidy vulgar fraction (⅛…⅞). */
function toFraction(quantity: number): string {
  const whole = Math.floor(quantity + 1e-6);
  const remainder = quantity - whole;
  const table: [number, string][] = [
    [0, ''],
    [0.125, '⅛'],
    [0.25, '¼'],
    [0.333, '⅓'],
    [0.5, '½'],
    [0.667, '⅔'],
    [0.75, '¾'],
    [0.875, '⅞'],
    [1, ''],
  ];
  let best: [number, string] = [0, ''];
  let bestDistance = 9;
  for (const entry of table) {
    const distance = Math.abs(remainder - entry[0]);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = entry;
    }
  }
  let wholePart = whole;
  let fraction = best[1];
  if (best[0] === 1) {
    wholePart += 1;
    fraction = '';
  }
  if (wholePart === 0 && fraction === '') return `${Math.round(quantity * 100) / 100}`;
  if (wholePart === 0) return fraction;
  return fraction ? `${wholePart} ${fraction}` : `${wholePart}`;
}

/** Small counted units read best as fractions; larger metric amounts as decimals. */
function formatQuantity({ quantity, unit }: { quantity: number; unit: string }): string {
  if (quantity <= 0) return '0';
  const fractionalUnits = [
    '',
    'cup',
    'cups',
    'tbsp',
    'tsp',
    'clove',
    'cloves',
    'can',
    'cans',
    'pinch',
    'sprig',
    'sprigs',
  ];
  if (fractionalUnits.includes(unit.toLowerCase()) && quantity < 24) return toFraction(quantity);
  if (quantity < 10) return `${Math.round(quantity * 10) / 10}`;
  return `${Math.round(quantity)}`;
}

function formatIngredient({
  ingredient,
  quantity,
}: {
  ingredient: RecipeIngredient;
  quantity: number;
}): string {
  return `${formatQuantity({ quantity, unit: ingredient.unit })}${ingredient.unit ? ` ${ingredient.unit}` : ''} ${ingredient.name}`;
}

/** "12", " 12 " → 12; anything else (blank, "1.5", "abc") → null. */
function parseWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return parseInt(trimmed, 10);
}

/** Remove the item at `from` and reinsert it at `to` — one row's drag-reorder. */
function moveItem<T>({ items, from, to }: { items: T[]; from: number; to: number }): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return items;
  next.splice(to, 0, moved);
  return next;
}

const MONO_LABEL: CSSProperties = {
  font: "500 10px 'JetBrains Mono',monospace",
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
};

const TITLE_STYLE: CSSProperties = {
  font: "700 26px 'Hanken Grotesk',sans-serif",
  margin: '0 0 18px',
  letterSpacing: '-0.02em',
  color: C.text,
};

const TAG_STYLE: CSSProperties = {
  padding: '3px 8px',
  borderRadius: 6,
  background: 'rgba(255,255,255,0.05)',
  color: C.text2,
  font: "600 10.5px 'JetBrains Mono',monospace",
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const addTagTriggerStyle: CSSProperties = {
  ...TAG_STYLE,
  background: 'rgba(181,155,255,0.08)',
  border: '1px dashed rgba(181,155,255,0.4)',
  color: '#d9ccff',
  cursor: 'pointer',
};

const draftTagStyle: CSSProperties = {
  ...TAG_STYLE,
  background: 'rgba(227,198,115,0.14)',
  border: '1px dashed rgba(227,198,115,0.5)',
  color: C.warn,
  cursor: 'pointer',
};

const completeTagStyle: CSSProperties = {
  ...TAG_STYLE,
  background: 'rgba(127,209,163,0.12)',
  border: '1px solid rgba(127,209,163,0.35)',
  color: C.good,
  cursor: 'pointer',
};

const statPickedTriggerStyle: CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  font: "700 17px 'Hanken Grotesk',sans-serif",
  color: C.text,
  cursor: 'pointer',
};

const statEmptyTriggerStyle: CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  borderBottom: '1px dashed rgba(181,155,255,0.5)',
  font: "600 14px 'Hanken Grotesk',sans-serif",
  color: '#d9ccff',
  cursor: 'pointer',
};

const pickerPopStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  zIndex: 10,
  width: 'max-content',
  minWidth: 210,
  maxWidth: 340,
  background: '#1b1b1e',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 13,
  padding: 13,
  boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
  textAlign: 'left',
};

const chipOnStyle: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(181,155,255,0.42)',
  background: 'rgba(181,155,255,0.12)',
  color: '#d9ccff',
  font: "600 12.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const chipOffStyle: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'transparent',
  color: C.text3,
  font: "600 12.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const statInputStyle: CSSProperties = {
  width: 56,
  boxSizing: 'border-box',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px dashed rgba(255,255,255,0.18)',
  padding: 0,
  font: "700 17px 'Hanken Grotesk',sans-serif",
  color: C.text,
  outline: 'none',
};

const statSuffixStyle: CSSProperties = {
  font: "500 11px 'Hanken Grotesk',sans-serif",
  color: C.text3,
};

const dialTileStyle: CSSProperties = {
  flex: 1,
  minWidth: 210,
  background: '#0f0f11',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 12,
  padding: '14px 16px',
};

const stepperStyle: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 9,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'transparent',
  color: C.text,
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
};

const ingredientRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '9px 0',
  borderBottom: '1px solid rgba(255,255,255,0.05)',
  font: "500 14px 'Hanken Grotesk',sans-serif",
  color: C.text,
};

const ingredientDotStyle: CSSProperties = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: 'rgba(181,155,255,0.65)',
  flexShrink: 0,
};

const spiceBadgeStyle: CSSProperties = {
  padding: '2px 7px',
  borderRadius: 5,
  background: 'rgba(224,137,122,0.14)',
  color: C.low,
  font: "600 9.5px 'JetBrains Mono',monospace",
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const stepNumberStyle: CSSProperties = {
  width: 25,
  height: 25,
  flexShrink: 0,
  borderRadius: '50%',
  background: 'rgba(181,155,255,0.14)',
  color: '#c9b6ff',
  font: "700 12px 'Hanken Grotesk',sans-serif",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const stepTextStyle: CSSProperties = {
  font: "400 14px/1.55 'Hanken Grotesk',sans-serif",
  color: '#c9c7c0',
  paddingTop: 2,
};

const rowInputStyle: CSSProperties = {
  boxSizing: 'border-box',
  height: 30,
  padding: '0 8px',
  borderRadius: 7,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f0f11',
  color: C.text,
  font: "500 13px 'Hanken Grotesk',sans-serif",
  outline: 'none',
};

const rowSelectStyle: CSSProperties = {
  ...rowInputStyle,
  // Wide enough for "custom…" plus the native dropdown arrow.
  width: 90,
  cursor: 'pointer',
};

const dragHandleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  flexShrink: 0,
  color: C.text3,
  cursor: 'grab',
};

const rowIconButtonStyle: CSSProperties = {
  width: 30,
  height: 30,
  flexShrink: 0,
  borderRadius: 7,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'transparent',
  color: C.text2,
  fontSize: 15,
  lineHeight: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const insertSelectStyle: CSSProperties = {
  flexShrink: 0,
  height: 26,
  padding: '0 8px',
  borderRadius: 8,
  border: '1px dashed rgba(255,255,255,0.16)',
  background: 'transparent',
  color: C.text2,
  font: "600 12px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const addRowButtonStyle: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '8px 13px',
  borderRadius: 9,
  border: '1px dashed rgba(255,255,255,0.16)',
  background: 'transparent',
  color: C.text2,
  font: "600 12.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'transparent',
  color: '#c9c7c0',
  font: "600 13px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

const saveButtonStyle: CSSProperties = {
  padding: '10px 17px',
  borderRadius: 10,
  border: '1px solid rgba(181,155,255,0.35)',
  background: 'rgba(181,155,255,0.14)',
  color: '#d9ccff',
  font: "600 13.5px 'Hanken Grotesk',sans-serif",
  cursor: 'pointer',
};

function GripIcon() {
  return (
    <svg viewBox="0 0 10 16" width="10" height="16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="3" r="1.4" />
      <circle cx="7" cy="3" r="1.4" />
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="7" cy="8" r="1.4" />
      <circle cx="3" cy="13" r="1.4" />
      <circle cx="7" cy="13" r="1.4" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="34"
      height="34"
      fill="none"
      stroke="#3f3e3a"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}
